import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { isNativeApp } from '../../utils/platform';

const EarningsBanner = () => {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if banner was dismissed this month
    const dismissedDate = localStorage.getItem('earningsBannerDismissed');
    if (dismissedDate) {
      const dismissed = new Date(dismissedDate);
      const now = new Date();
      
      // Check if it's still the same month
      if (dismissed.getMonth() === now.getMonth() && 
          dismissed.getFullYear() === now.getFullYear()) {
        setDismissed(true);
        setLoading(false);
        return;
      } else {
        // New month, clear the dismissal
        localStorage.removeItem('earningsBannerDismissed');
      }
    }

    fetchBannerData();
  }, []);

  const fetchBannerData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/dashboard/earnings-banner`
      );
      setBannerData(response.data);
    } catch (error) {
      console.error('Error fetching earnings banner:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('earningsBannerDismissed', new Date().toISOString());
  };

  const handleUpgrade = () => {
    navigate('/driver/subscription');
  };

  // Don't show if dismissed or loading or no data
  if (loading || dismissed || !bannerData) {
    return null;
  }

  // Determine banner color scheme
  const isUpsell = bannerData.shouldUpsell;
  const isCaptain = bannerData.isCaptain;

  return (
    <div 
      className={`sticky top-0 z-40 safe-top ${
        isCaptain
          ? 'bg-gradient-to-r from-green-500 to-green-600'
          : isUpsell
            ? 'bg-gradient-to-r from-blue-500 to-blue-600'
            : 'bg-gradient-to-r from-green-500 to-green-600'
      } text-white shadow-lg`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {/* Icon */}
            <div className="flex-shrink-0">
              {isUpsell ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>

            {/* Message */}
            <div className="flex-1">
              <p className="text-sm sm:text-base font-medium">
                {bannerData.message}
              </p>
              {isUpsell && (
                <p className="text-xs sm:text-sm opacity-90 mt-1">
                  You're currently on the <strong>{bannerData.tierName}</strong> plan
                </p>
              )}
            </div>

            {/* Action Button */}
            {isUpsell && !isNativeApp() && (
              <button
                onClick={handleUpgrade}
                className="hidden sm:block px-4 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors duration-200 flex-shrink-0"
              >
                Upgrade to Captain
              </button>
            )}
          </div>

          {/* Close Button */}
          <button
            onClick={handleDismiss}
            className="ml-4 flex-shrink-0 text-white hover:text-gray-200 transition-colors duration-200"
            aria-label="Dismiss banner"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile Upgrade Button */}
        {isUpsell && !isNativeApp() && (
          <button
            onClick={handleUpgrade}
            className="sm:hidden w-full mt-3 px-4 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors duration-200"
          >
            Upgrade to Captain
          </button>
        )}
      </div>
    </div>
  );
};

export default EarningsBanner;